import { useState } from 'react'
import { Button, Card, CardBody, CardHeader, Chip } from '@heroui/react'

import { AlgoliaService } from '../services'
import { useToast } from '../../../hooks'

interface ReindexResult {
  count: number
  indexName: string
  processingTimeMs: number
}

export function AlgoliaManagement() {
  const [isReindexing, setIsReindexing] = useState(false)
  const [reindexResult, setReindexResult] = useState<ReindexResult | null>(null)
  const [reindexError, setReindexError] = useState<string | null>(null)
  const toast = useToast()

  const handleReindex = async () => {
    setIsReindexing(true)
    setReindexError(null)
    setReindexResult(null)

    try {
      toast.info('Starting full reindex...', { duration: 3000 })

      const result = await AlgoliaService.reindexGifs()

      if (result.success && result.data) {
        setReindexResult(result.data)
        toast.success(
          `Reindexing completed! ${result.data.count} documents processed in ${AlgoliaService.formatProcessingTime(result.data.processingTimeMs)}`
        )
      } else {
        setReindexError(result.error || 'Reindexing failed')
        toast.error(`Reindexing failed: ${result.error || 'Unknown error'}`)
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error occurred'
      setReindexError(errorMessage)
      toast.error(`Reindexing failed: ${errorMessage}`)
    } finally {
      setIsReindexing(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Status Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <h3 className="text-lg font-semibold">Search Index Status</h3>
            <Chip color="success" variant="flat">
              Connected
            </Chip>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {reindexResult ? reindexResult.count : '~'}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Total Records
              </div>
              <div className="text-xs text-slate-500">
                {reindexResult ? 'Last reindex' : 'Coming soon'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                {reindexResult
                  ? AlgoliaService.formatProcessingTime(
                      reindexResult.processingTimeMs
                    )
                  : '~'}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Processing Time
              </div>
              <div className="text-xs text-slate-500">
                {reindexResult ? 'Last reindex' : 'Coming soon'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                {reindexResult ? reindexResult.indexName : '~'}
              </div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                Index Name
              </div>
              <div className="text-xs text-slate-500">
                {reindexResult ? 'Active index' : 'Coming soon'}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Reindexing Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Reindexing Operations</h3>
            {isReindexing && (
              <Chip color="primary" variant="flat" size="sm">
                Processing...
              </Chip>
            )}
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            Trigger a full reindex of the search database. This will clear the
            existing index and re-upload all GIF documents with the latest data
            from Firestore.
          </p>

          <div className="flex items-center gap-4">
            <Button
              color="primary"
              onPress={handleReindex}
              className="font-medium"
              isLoading={isReindexing}
              isDisabled={isReindexing}
            >
              {isReindexing ? 'Reindexing...' : 'Start Full Reindex'}
            </Button>
            <div className="text-sm text-slate-500">
              ⚠️ This operation may take several minutes
            </div>
          </div>

          {/* Error Display */}
          {reindexError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950 dark:border-red-800">
              <div className="flex items-start gap-2">
                <div className="text-red-600 dark:text-red-400 mt-1">❌</div>
                <div>
                  <h4 className="font-medium text-red-800 dark:text-red-200">
                    Reindexing Failed
                  </h4>
                  <p className="text-sm text-red-700 dark:text-red-300">
                    {reindexError}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Success Display */}
          {reindexResult && !reindexError && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 dark:bg-green-950 dark:border-green-800">
              <div className="flex items-start gap-2">
                <div className="text-green-600 dark:text-green-400 mt-1">
                  ✅
                </div>
                <div>
                  <h4 className="font-medium text-green-800 dark:text-green-200">
                    Reindexing Complete
                  </h4>
                  <p className="text-sm text-green-700 dark:text-green-300">
                    Successfully processed {reindexResult.count} documents in{' '}
                    {AlgoliaService.formatProcessingTime(
                      reindexResult.processingTimeMs
                    )}
                    . Index &quot;{reindexResult.indexName}&quot; is now up to
                    date.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Future Features Placeholder */}
      <Card>
        <CardHeader>
          <h3 className="text-lg font-semibold">Additional Tools</h3>
        </CardHeader>
        <CardBody>
          <div className="space-y-3">
            <div className="flex items-center gap-3 opacity-50">
              <Chip size="sm" variant="flat" color="default">
                TBD
              </Chip>
              <span className="text-sm text-slate-500">
                Index performance metrics
              </span>
            </div>
            <div className="flex items-center gap-3 opacity-50">
              <Chip size="sm" variant="flat" color="default">
                TBD
              </Chip>
              <span className="text-sm text-slate-500">Search analytics</span>
            </div>
            <div className="flex items-center gap-3 opacity-50">
              <Chip size="sm" variant="flat" color="default">
                TBD
              </Chip>
              <span className="text-sm text-slate-500">
                Index configuration management
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
